import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';

import { LoggerContext } from '@ph-core/enums/logger-context.enum';
import { AuthFlowTraceService } from '@ph-core/services/auth/auth-flow-trace.service';
import { LoggerService } from '@ph-core/services/logger/logger.service';
import { RouterService } from '@ph-core/services/router/router.service';

import { AuthService } from '../services/auth/auth.service';

/**
 * Authentication/session guard for the login route.
 * Allows unauthenticated users to access login and routes authenticated users to app content.
 * For Okta SSO, sends first-time login visits to `/callback` for silent SSO before showing the widget.
 */
export const loginGuard: CanActivateFn = (route) => {
  const routerService = inject(RouterService);
  const authService = inject(AuthService);
  const logger = inject(LoggerService);
  const authFlowTrace = inject(AuthFlowTraceService);

  const passwordToken = route.queryParamMap.get('passwordToken');
  const passwordTokenMeta = {
    hasPasswordToken: !!passwordToken,
    tokenLength: passwordToken?.length ?? 0,
    tokenSuffix: passwordToken ? passwordToken.slice(-4) : null,
    queryKeys: route.queryParamMap.keys,
  };

  // Password-reset deep links (/login?passwordToken=...) must reach the login page so the reset
  // popup can open; skip the auth/silent-SSO redirects that would strip the query param.
  if (passwordToken) {
    logger.log(LoggerContext.Auth, 'loginGuard: allowing login for password-reset deep link', passwordTokenMeta);
    authFlowTrace.record('login-guard', 'loginGuard:allow-password-reset', passwordTokenMeta, '/login');
    return true;
  }

  logger.log(LoggerContext.Auth, 'loginGuard: evaluating auth/silent-SSO', passwordTokenMeta);
  authFlowTrace.record('login-guard', 'loginGuard:evaluate', passwordTokenMeta, '/login');

  return authService
    .isAuthenticated()
    .then((isAuthenticated) => {
      if (isAuthenticated) {
        logger.log(LoggerContext.Auth, 'loginGuard: already authenticated — navigateToContent');
        authFlowTrace.record('login-guard', 'loginGuard:redirect-content', { isAuthenticated: true }, '/login');
        routerService.navigateToContent();

        return false;
      }

      if (authService.shouldAttemptSilentSsoViaCallback()) {
        logger.log(LoggerContext.Auth, 'loginGuard: redirecting to callback for silent SSO');
        authFlowTrace.record(
          'login-guard',
          'loginGuard:redirect-callback-silent-sso',
          { isAuthenticated: false },
          '/login'
        );
        routerService.navigateToCallback();

        return false;
      }

      logger.log(LoggerContext.Auth, 'loginGuard: allowing login page');
      authFlowTrace.record('login-guard', 'loginGuard:allow', { isAuthenticated: false }, '/login');
      return true;
    })
    .catch((error: unknown) => {
      logger.warn(LoggerContext.Auth, 'loginGuard: isAuthenticated failed — allowing login', { error });
      authFlowTrace.record('login-guard', 'loginGuard:allow-on-error', { error: String(error) }, '/login');
      return true;
    });
};
